import * as Sentry from '@sentry/nextjs';

// Track API errors
export const trackApiError = (error: any, endpoint: string, method: string = 'GET') => {
  Sentry.withScope((scope) => {
    scope.setTag('type', 'api-error');
    scope.setTag('endpoint', endpoint);
    scope.setTag('method', method);

    scope.setContext('api', {
      endpoint,
      method,
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      responseData: error?.response?.data,
    });

    if (error?.response?.status) {
      scope.setLevel(error.response.status >= 500 ? 'error' : 'warning');
    } else {
      scope.setLevel('error');
    }

    Sentry.captureException(error);
  });
};

// Track GraphQL errors
export const trackGraphQLError = (error: any, operation: string, variables?: any) => {
  Sentry.withScope((scope) => {
    scope.setTag('type', 'graphql-error');
    scope.setTag('operation', operation);

    scope.setContext('graphql', {
      operation,
      variables,
      errors: error?.graphQLErrors,
      networkError: error?.networkError,
    });

    scope.setLevel('error');
    Sentry.captureException(error);
  });
};

// Track authentication errors
export const trackAuthError = (error: any, action: string) => {
  Sentry.withScope((scope) => {
    scope.setTag('type', 'auth-error');
    scope.setTag('action', action);

    scope.setContext('auth', {
      action,
      errorCode: error?.code,
      errorMessage: error?.message,
    });

    scope.setLevel('warning');
    Sentry.captureException(error);
  });
};

// Track user actions for better debugging
export const trackUserAction = (action: string, component: string, data?: Record<string, any>) => {
  Sentry.addBreadcrumb({
    message: `User ${action} in ${component}`,
    category: 'user-action',
    level: 'info',
    data: {
      action,
      component,
      ...data,
    },
    timestamp: Date.now() / 1000,
  });
};

// Track performance issues
export const trackPerformanceIssue = (name: string, duration: number, threshold: number = 1000) => {
  if (duration > threshold) {
    Sentry.withScope((scope) => {
      scope.setTag('type', 'performance-issue');
      scope.setTag('operation', name);

      scope.setContext('performance', {
        operation: name,
        duration,
        threshold,
        exceeded: duration - threshold,
      });

      scope.setLevel('warning');
      Sentry.captureMessage(
        `Performance issue: ${name} took ${duration}ms (threshold: ${threshold}ms)`
      );
    });
  }
};

// Set user context from user object
export const setSentryUser = (user: any) => {
  Sentry.setUser({
    id: user?.id,
    email: user?.email,
    username: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : undefined,
    organizationId: user?.organizationID,
  });
};

// Clear user context (on logout)
export const clearSentryUser = () => {
  Sentry.setUser(null);
};

// Track custom events
export const trackEvent = (eventName: string, data?: Record<string, any>) => {
  Sentry.addBreadcrumb({
    message: eventName,
    category: 'custom-event',
    level: 'info',
    data,
    timestamp: Date.now() / 1000,
  });
};
